import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Descriptions, Empty, Message, Popconfirm, Space, Timeline, Typography } from '@arco-design/web-react';
import { IconBranch, IconMessage } from '@arco-design/web-react/icon';
import BccTabs, { BccTabPane } from './BccTabs';
import DetailDrawer, { DRAWER_WIDTH } from './DetailDrawer';
import DetailSection from './DetailSection';
import PillTag from './PillTag';
import RunQualityModal from './RunQualityModal';
import StatusTag from './StatusTag';
import TabTitle from './TabTitle';
import ChatMessageBubble from './ChatMessageBubble';
import { SkeletonTable } from './Skeleton';
import EmptyState from './EmptyState';
import {
  getFlowTrail,
  getSession,
  listMessages,
  type ChatFlowTrail,
  type ChatMessage,
  type ChatSession,
  type FlowTrailEvent,
} from '../api/chat';
import { fmtTime } from '../utils/format';
import { FLOW_TRAIL_EVENT_LABELS, FLOW_TRAIL_NODE_LABELS, chatChannelMeta } from '../utils/labels';
import { FLOW_STATE_LABELS } from '../api/cdr';
import { listReports, getReport, type QualityReport } from '../api/quality';
import { usePerm } from '../hooks/usePerm';
import { monitorChatAction } from '../api/monitor';
import { useAgentStore } from '../store/agent';
import { useAuthStore } from '../store/auth';

// 完整报告弹层按需加载：报告详情本体在 pages/quality，动态导入避免 components ↔ pages 静态循环
const ReportDetailModal = lazy(() => import('../pages/quality/ReportDetailModal'));

/** 流程轨迹节点名解析：biz_flow_node.name 含中文优先（用户自定义节点名），否则常见 key 映射兜底 */
function nodeNameOf(ev: FlowTrailEvent): string {
  const n = ev.node_name || ev.payload?.node_name || '';
  if (/[\u4e00-\u9fa5]/.test(n)) return n;
  return FLOW_TRAIL_NODE_LABELS[ev.node_key] || n || ev.node_key;
}

/** 流程轨迹单条事件渲染（会话详情「流程轨迹」时间线） */
function FlowTrailItem({ ev }: { ev: FlowTrailEvent }) {
  const label = FLOW_TRAIL_EVENT_LABELS[ev.event] || ev.event;
  const prompt = ev.payload?.prompt as string | undefined;
  const flow = ev.payload?.flow as string | undefined;
  // 中文名优先：历史拉取顶层 node_name/flow_name，实时增量在 payload 内；node_key 常见英文兜底映射
  const nodeName = nodeNameOf(ev);
  const flowName = ev.flow_name || ev.payload?.flow_name;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <PillTag color="arcoblue" size="sm">
          {label}
        </PillTag>
        {ev.node_key && (
          <PillTag color="arcoblue" size="sm">
            {nodeName}
          </PillTag>
        )}
        {(flowName || flow) && (
          <PillTag color="gray" size="sm">
            {flowName || flow}
          </PillTag>
        )}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {fmtTime(ev.ts)}
        </Typography.Text>
      </div>
      {prompt && (
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
          {prompt}
        </Typography.Text>
      )}
    </div>
  );
}

export interface ChatSessionDrawerProps {
  visible: boolean;
  /** 要展示详情的会话 id（null/undefined 不加载） */
  sessionId?: number | null;
  onClose: () => void;
}

/**
 * 会话详情右侧抽屉（会话记录页与客户管理「客户动态」会话条目共用）：
 * 按 sessionId 自加载会话信息 + 坐席/机器人小结 + 消息记录 + 流程轨迹。
 * 与呼叫记录 CallDetailDrawer / 工单 TicketDetailDrawer 同口径，可在页面内直接叠加打开。
 */
export default function ChatSessionDrawer({ visible, sessionId, onClose }: ChatSessionDrawerProps) {
  const [session, setSession] = useState<ChatSession | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [trail, setTrail] = useState<ChatFlowTrail | null>(null);
  const [trailLoading, setTrailLoading] = useState(false);
  // 单条质检：该会话最新报告 + 触发/轮询状态（仅管理端 quality.manage 可见入口）
  const canManageQuality = usePerm('quality.manage');
  const [qualityReport, setQualityReport] = useState<QualityReport | null>(null);
  const [qualityPolling, setQualityPolling] = useState(false);
  const [runQualityOpen, setRunQualityOpen] = useState(false);
  // 完整质检报告弹窗（footer「查看报告」入口）
  const [qualityModalId, setQualityModalId] = useState<number | null>(null);
  // 会话内容「关注」：进行中且承办给他人的会话，消息/轨迹只对关注者开放（后端 404）。
  // 关注态按 sessionId 记，且只在后端 200 之后落地（能不能关注由后端裁决，本地不预判权限）。
  const myAgentId = useAgentStore((s) => s.agentId) || 0;
  const myRole = useAuthStore((s) => s.user?.role);
  const [followed, setFollowed] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [contentDenied, setContentDenied] = useState(false);
  /** 当前关注着哪一路（收尾/切会话时要注销的 sessionId；state 只用于渲染） */
  const followedRef = useRef<number | null>(null);

  /** 该会话内容是否需要先关注：admin（全量范围）、本人承办、已结束（记录面）、排队池都不需要 */
  const contentGated =
    !!session && myRole !== 'admin' && session.status !== 'closed' && !!session.agent_id && session.agent_id !== myAgentId;

  const onFollow = useCallback(async () => {
    if (!sessionId) return;
    setFollowBusy(true);
    try {
      await monitorChatAction(sessionId, 'listen', { silent: true });
      followedRef.current = sessionId;
      setFollowed(true);
    } catch (e: any) {
      // 失败必须给出原因：静默失败会让人以为按钮没点动（403=不在数据范围，409=已在关注另一路）
      Message.error(e?.msg || '关注会话失败');
    } finally {
      setFollowBusy(false);
    }
  }, [sessionId]);

  /** 结束关注：只在确实关注着某一路时才发（失败静默——收尾动作不该再弹提示） */
  const stopFollow = useCallback(() => {
    const sid = followedRef.current;
    followedRef.current = null;
    setFollowed(false);
    if (sid) monitorChatAction(sid, 'stop', { silent: true }).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!visible || !sessionId) {
      // 抽屉关闭即结束关注（与通话监听弹窗关闭即 stop 同口径）：否则订阅只剩 TTL 自然过期，
      // 会出现「界面已无关注标记、内容却还能被读」的窗口
      stopFollow();
      setSession(null);
      setTrail(null);
      return;
    }
    // 抽屉内在会话之间切换：上一路的关注不跟着新会话走（关注态按 sessionId 归属）
    if (followedRef.current && followedRef.current !== sessionId) stopFollow();
    let cancelled = false;
    setSession(null);
    setMessages([]);
    setTrail(null);
    setSessionLoading(true);
    getSession(sessionId)
      .then((s) => {
        if (!cancelled) setSession(s);
      })
      .catch(() => {
        if (!cancelled) setSession(null);
      })
      .finally(() => {
        if (!cancelled) setSessionLoading(false);
      });
    setMsgLoading(true);
    listMessages(sessionId, { page: 1, pageSize: 500 }, { silent: true })
      .then(({ list }) => {
        if (cancelled) return;
        setMessages(list || []);
        setContentDenied(false);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setMessages([]);
        // 404/403 是「未关注（或无权关注）」的预期分支：就地给关注入口，不弹全局错误
        const st = e?.response?.status;
        setContentDenied(st === 404 || st === 403);
      })
      .finally(() => {
        if (!cancelled) setMsgLoading(false);
      });
    setTrailLoading(true);
    getFlowTrail(sessionId, { silent: true })
      .then((t) => {
        if (!cancelled) setTrail(t || null);
      })
      .catch(() => {
        if (!cancelled) setTrail(null);
      })
      .finally(() => {
        if (!cancelled) setTrailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, sessionId, followed, stopFollow]);

  // 该会话最新质检报告：先按会话查列表定位报告 id，再取详情（列表项不含违规点/维度明细对象），
  // 详情失败回退列表项，保证面板仍能显示总分
  const loadQualityReport = useCallback(async (id: number) => {
    const { list } = await listReports({ ref_chat_id: id, scene: 'chat', page: 1, pageSize: 5 });
    const first = list[0];
    if (!first) return null;
    try {
      return await getReport(first.id);
    } catch {
      return first;
    }
  }, []);

  useEffect(() => {
    if (!visible || !sessionId) {
      setQualityReport(null);
      setQualityPolling(false);
      return;
    }
    let cancelled = false;
    setQualityReport(null);
    loadQualityReport(sessionId)
      .then((rep) => {
        if (!cancelled) setQualityReport(rep);
      })
      .catch(() => {
        if (!cancelled) setQualityReport(null);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, sessionId, loadQualityReport]);

  // 质检轮询：投递后每 3s 拉报告，报告出现即完成（最多约 5 分钟兜底）
  useEffect(() => {
    if (!qualityPolling || !visible || !sessionId) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const rep = await loadQualityReport(sessionId);
        if (!alive) return;
        if (rep) {
          setQualityReport(rep);
          setQualityPolling(false);
          Message.success('质检完成');
          return;
        }
      } catch {
        /* 轮询静默失败：继续下一轮 */
      }
      if (!alive) return;
      if (attempts >= 100) {
        setQualityPolling(false);
        return;
      }
      timer = setTimeout(tick, 3000);
    };
    timer = setTimeout(tick, 3000);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [qualityPolling, visible, sessionId, loadQualityReport]);

  return (
    <DetailDrawer
      width={DRAWER_WIDTH.wide}
      title={`会话 #${sessionId ?? ''} 消息记录`}
      visible={visible}
      onCancel={onClose}
      unmountOnExit
      // 首屏加载态收口到抽屉壳的骨架（此前在正文里自建居中 Spin，与其它抽屉两种观感）；
      // 加载中 children 必须为空，否则"空态文案 + 加载指示"会同帧出现
      loading={sessionLoading && !session}
      footer={
        session ? (
          <Space style={{ justifyContent: 'flex-end' }}>
            {qualityReport && (
              <Button onClick={() => setQualityModalId(qualityReport.id)}>查看报告</Button>
            )}
            {canManageQuality &&
              (qualityReport ? (
                <Popconfirm
                  title="该会话已有质检报告，重新质检会一并删除现有报告与复核样本，且不可恢复。确认继续？"
                  okText="继续"
                  cancelText="取消"
                  onOk={() => setRunQualityOpen(true)}
                >
                  <Button loading={qualityPolling}>质检</Button>
                </Popconfirm>
              ) : (
                <Button loading={qualityPolling} onClick={() => setRunQualityOpen(true)}>
                  质检
                </Button>
              ))}
          </Space>
        ) : undefined
      }
    >
      {sessionLoading && !session ? null : session ? (
        <>
          {/* 会话字段列表与其他弹层正文同口径挂分节标题；小结条/消息页签各自已带标题，不再套一层 */}
          <DetailSection title="会话信息">
            <Descriptions
              column={2}
              size="small"
              style={{ marginBottom: 16 }}
              data={[
                { label: '访客', value: session.visitor_name || '-' },
                { label: '状态', value: <StatusTag value={session.status} /> },
                { label: '接待坐席', value: session.agent_name || '-' },
                {
                  label: '渠道',
                  value: chatChannelMeta(session.channel)[0],
                },
                { label: '开始', value: fmtTime(session.created_at) },
                { label: '结束', value: fmtTime(session.closed_at) },
              ]}
            />
          </DetailSection>
          {/* 关注态可见标记：关注中的会话，消息/轨迹才读得到；结束关注即回到受限态 */}
          {followed && (
            <Space size={8} style={{ marginBottom: 8 }}>
              <PillTag color="green" size="sm">
                关注中
              </PillTag>
              <Button size="mini" onClick={stopFollow}>
                结束关注
              </Button>
            </Space>
          )}
          {session.summary && (
            <div className="bcc-chat-summary agent">
              <Typography.Text bold style={{ marginRight: 8, color: 'var(--bcc-text-2)' }}>
                坐席小结
              </Typography.Text>
              <Typography.Text>{session.summary}</Typography.Text>
            </div>
          )}
          {session.bot_summary && (
            <div className="bcc-chat-summary bot">
              <Typography.Text bold style={{ marginRight: 8, color: 'var(--bcc-ai)' }}>
                机器人小结
              </Typography.Text>
              <Typography.Text>{session.bot_summary}</Typography.Text>
            </div>
          )}
          <BccTabs contentPadding={false} size="small" style={{ marginTop: 4 }}>
            <BccTabPane
              key="msgs"
              title={<TabTitle icon={<IconMessage />} label={`消息记录（${messages.length}）`} />}
            >
              <div className="bcc-chat-msgs">
                {msgLoading ? (
                  <SkeletonTable rows={4} cols={1} />
                ) : contentGated && !followed ? (
                  // 进行中且承办给他人的会话：内容面只对关注者开放（后端 404）。
                  // 在这里给一次「关注」入口，否则本抽屉对这类会话只有一个「暂无消息」的死胡同
                  // （口径与工作台「本会话对话」一致：本人承办 / admin / 已结束 / 排队池都不需要关注）。
                  <EmptyState
                    description="该会话由他人承办，查看对话内容需先关注"
                    action={
                      <Button type="primary" loading={followBusy} onClick={onFollow}>
                        关注会话
                      </Button>
                    }
                  />
                ) : messages.length === 0 ? (
                  <Empty description="暂无消息" />
                ) : (
                  messages.map((m, i) => <ChatMessageBubble key={m.id ?? i} m={m} />)
                )}
              </div>
            </BccTabPane>
            <BccTabPane key="trail" title={<TabTitle icon={<IconBranch />} label="流程轨迹" />}>
              <div style={{ minHeight: 300, padding: '4px 8px' }}>
                {trailLoading ? (
                  <SkeletonTable rows={4} cols={2} />
                ) : !trail || trail.events.length === 0 ? (
                  <Empty description="该会话无流程轨迹" />
                ) : (
                  <>
                    <div style={{ marginBottom: 12 }}>
                      <PillTag color="arcoblue">{trail.flow_name || trail.flow_code || `流程 #${trail.flow_id}`}</PillTag>
                      <PillTag color={trail.state === 'completed' ? 'green' : 'orange'}>
                        {FLOW_STATE_LABELS[trail.state] || trail.state}
                      </PillTag>
                    </div>
                    <Timeline>
                      {trail.events.map((e, i) => (
                        <Timeline.Item key={i}>
                          <FlowTrailItem ev={e} />
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  </>
                )}
              </div>
            </BccTabPane>
          </BccTabs>
        </>
      ) : (
        <Empty description="未找到会话记录" />
      )}
      {/* 查看完整质检报告（footer「查看报告」入口；详情变更后刷新面板） */}
      {qualityModalId != null && (
        <Suspense fallback={null}>
          <ReportDetailModal
            reportId={qualityModalId}
            visible
            onClose={() => setQualityModalId(null)}
            onChanged={() => {
              if (sessionId) void loadQualityReport(sessionId).then(setQualityReport).catch(() => undefined);
            }}
          />
        </Suspense>
      )}
      {/* 单条质检：规则集选择 → 异步投递 worker → 轮询报告 */}
      <RunQualityModal
        visible={runQualityOpen}
        scene="chat"
        sessionId={sessionId}
        hasReport={!!qualityReport}
        onCancel={() => setRunQualityOpen(false)}
        onSubmitted={() => {
          setRunQualityOpen(false);
          setQualityReport(null);
          setQualityPolling(true);
        }}
      />
    </DetailDrawer>
  );
}
